import moment from 'moment'
import { CreateProposalClientRequest } from '../../../@types/CreateProposal'
import { MatchedEntity } from '../../../@types/GetEntitiesDetails'
import {
  DiscountCampaings,
  EntityData,
} from '../../../@types/RunPetisSimulation'
import { useAnimalStore } from '../../../Stores/animalStore'
import { useReportStore } from '../../../Stores/reportStore'
import { TODAY, useSimulatorStore } from '../../../Stores/simulatorStore'
import { useAgent } from '../../../Stores/useAgent'
import { useEntityDetailsForSummaryDocumentStore } from '../../../Stores/useEntityDetailsForSummaryDocumentStore'
import { useFillDataForSimulationStore } from '../../../Stores/useFillDataForSimulationStore'
import { useResumeStore } from '../../../Stores/useResumeStore'
import { useRunPetisSimulationStore } from '../../../Stores/useRunPetisSimulationStore'
import { useSimulationSummaryDownloadStore } from '../../../Stores/useSimulationSummaryDownloadStore'
import { mountedEntityData, mountedInsuredObjectGroup } from './utilsPayload'

export const useCreateProposalPayload = () => {
  const { agentIdBySessionStorage } = useAgent()
  const { fillDataForSimulationRequest } = useFillDataForSimulationStore()
  const { isResume } = useResumeStore()
  const animalsList = useAnimalStore((state) => state.animalsList)
  const {
    entity,
    entitySelected,
    initialDate,
    endDate,
    typeOfPrice,
    entitySelectedAddress,
  } = useSimulatorStore()
  const { simulationSummaryDownload } = useSimulationSummaryDownloadStore()
  const { report } = useReportStore()
  const { entityDetailsForSummaryDocumentSelected } =
    useEntityDetailsForSummaryDocumentStore()
  const { runPetisSimulation, selectedPackCampaign } =
    useRunPetisSimulationStore((state) => ({
      runPetisSimulation: state.runPetisSimulation,
      selectedPackCampaign: state.selectedPackCampaign,
    }))

  const campaigns = runPetisSimulation?.discountsCampaigns?.filter(
    (campaign) => campaign.campaignId === selectedPackCampaign?.campaignId,
  )

  const handleDates = () => {
    const _initialDateMoment = moment(initialDate, 'DD-MM-YYYY')
    const currentDate = moment(TODAY, 'DD-MM-YYYY')

    if (isResume && _initialDateMoment.isBefore(currentDate)) {
      return {
        startDate: moment(currentDate).format(),
        endDate: moment(TODAY, 'DD-MM-YYYY')
          .add(1, 'year')
          .subtract(1, 'days')
          .format(),
        effectiveDate: moment(currentDate).format('YYYY-MM-DD'),
        quoteDate: moment().format(),
      }
    }

    return {
      startDate: moment(initialDate, 'DD-MM-YYYY').format(),
      endDate,
      effectiveDate: moment(initialDate, 'DD-MM-YYYY').format('YYYY-MM-DD'),
      quoteDate: moment(
        runPetisSimulation?.runPetisSimulationResponse?.quoteDate as string,
      ).format(),
    }
  }

  const handleCreateProposal = () => {
    const insuredObjectGroup = mountedInsuredObjectGroup(
      animalsList,
      runPetisSimulation?.runPetisSimulationResponse!,
      typeOfPrice,
      selectedPackCampaign!,
    )

    const getDiscounts = () => {
      let discounts = [] as DiscountCampaings[]

      animalsList?.forEach((animal) => {
        if (selectedPackCampaign?.coverages !== null) {
          const discountsByAnimal = (campaigns || [])?.map((campaign) => ({
            ...campaign,
            insuredObjectId: animal.id,
            objectGroupId:
              runPetisSimulation?.runPetisSimulationResponse?.insuredObjectGroup
                ?.insuredObjectGroupId || '1',
            isSelected: true,
            coverages: (campaign?.coverages || [])?.map((coverage) => ({
              ...coverage,
              value: coverage.maxDiscount,
            })),
          })) as DiscountCampaings[]

          discounts = [...discounts, ...discountsByAnimal]
        }
      })

      return discounts.length > 0 ? discounts : null
    }

    const payload: CreateProposalClientRequest = {
      discounts: getDiscounts() as DiscountCampaings[],
      quoteId: runPetisSimulation?.runPetisSimulationResponse?.quoteId,
      quoteDate: handleDates().quoteDate,
      startDate: handleDates().startDate,
      endDate: handleDates().endDate,
      effectiveDate: handleDates().effectiveDate,
      pricingCallId: '', // TODO: Não tem no momento
      insuredPeriod: 'Y', // TODO: Não tem no momento
      packInstallment: typeOfPrice || '',
      agent: {
        startDate: moment(initialDate, 'DD-MM-YYYY').format(),
        endDate,
        externalId: agentIdBySessionStorage || '',
        agentRole:
          fillDataForSimulationRequest?.agentDetailsResponse?.agentRole || '',
        agentCode:
          fillDataForSimulationRequest?.agentDetailsResponse?.agentCode || '',
        inspectorCode:
          fillDataForSimulationRequest?.agentDetailsResponse
            ?.agentInspectorCode || '',
        hasDefaultComission: true, // TODO: Não tem no momento
        commission: 0, // TODO: Não tem no momento
        negPercentage: 0, // TODO: Não tem no momento
      },
      entityData: mountedEntityData(
        entity,
        entitySelected as MatchedEntity,
        endDate,
        entitySelectedAddress,
        entityDetailsForSummaryDocumentSelected,
        runPetisSimulation?.runPetisSimulationResponse
          ?.policyHolder as EntityData,
        simulationSummaryDownload,
        report,
      ),
      insuredObjectGroup,
      productData: {
        lineOfBusiness: {
          countryId:
            runPetisSimulation?.runPetisSimulationResponse?.productData
              ?.lineofBusiness?.countryID || '',
          lineOfBusinessId:
            runPetisSimulation?.runPetisSimulationResponse?.productData
              ?.lineofBusiness?.lineofBusinessID || '',
          productFamilyId:
            runPetisSimulation?.runPetisSimulationResponse?.productData
              ?.lineofBusiness?.productFamilyID || '',
          productTypeId:
            runPetisSimulation?.runPetisSimulationResponse?.productData
              ?.lineofBusiness?.productTypeID || '',
        },
        brand:
          runPetisSimulation?.runPetisSimulationResponse?.productData?.brand ||
          '',
        company:
          runPetisSimulation?.runPetisSimulationResponse?.productData
            ?.company || '',
        distributionChannel:
          runPetisSimulation?.runPetisSimulationResponse?.productData
            ?.distributionChannel || '',
        product:
          runPetisSimulation?.runPetisSimulationResponse?.productData
            ?.product || '',
        productCode:
          runPetisSimulation?.runPetisSimulationResponse?.productData
            ?.productCode || '',
        segment: {
          segmentCode:
            runPetisSimulation?.runPetisSimulationResponse?.productData?.segment
              ?.segmentCode || '',
        },
      },
    }

    return payload
  }

  return {
    createProposalPayload: handleCreateProposal(),
  }
}
